
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbList, BreadcrumbLink, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { measurementTypeFacts } from "@/components/unit-converter/data/measurement-type-facts";
import { unitSpecificFacts } from "@/components/unit-converter/data/unit-specific-facts";
import { AdUnit } from "@/components/ads/AdUnit";
import { Helmet } from "react-helmet";

export default function UnitHistory() {
  const { unit } = useParams<{ unit: string }>();
  const [unitName, setUnitName] = useState<string | null>(null);
  const [historyText, setHistoryText] = useState<string | null>(null);
  const [funFacts, setFunFacts] = useState<string[]>([]);

  useEffect(() => {
    if (unit) {
      const formattedUnit = unit.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase();
      const unitData = unitSpecificFacts[formattedUnit];

      if (unitData) {
        setUnitName(unitData.name);
        setHistoryText(unitData.history || null);
        setFunFacts(unitData.funFacts || []);
      } else {
        setUnitName(unit);
        setHistoryText(null);
        setFunFacts([]);
      }
    }
  }, [unit]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [unit]);

  // Add the following import and schema implementation within the component
  // This would go near the top of the main component function

  return (
    <div className="min-h-screen bg-manuscript-background py-8">
      <Helmet>
        <title>{unitName ? `${unitName} History & Conversion | UnitCodex` : 'Unit History | UnitCodex'}</title>
        <meta name="description" content={`Learn about the fascinating history of ${unitName || 'measurement units'} and their conversion to modern units.`} />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": `History of the ${unitName || 'Unit'} Measurement`,
            "description": `Learn about the origins and history of the ${unitName || 'unit'} measurement system and how it compares to modern units.`,
            "datePublished": "2023-08-01T12:00:00+00:00",
            "dateModified": "2023-08-01T12:00:00+00:00",
            "author": {
              "@type": "Organization",
              "name": "UnitCodex"
            },
            "publisher": {
              "@type": "Organization",
              "name": "UnitCodex",
              "logo": {
                "@type": "ImageObject",
                "url": "https://unitcodex.com/og-image.png"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://unitcodex.com/history/${unitName?.toLowerCase() || 'unit'}`
            }
          })}
        </script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-3xl">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/history">History</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{unitName || "Unit History"}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <header className="text-center my-8">
          <h1 className="text-4xl font-cinzel text-manuscript-primary mb-4">
            {unitName ? `The History of ${unitName}` : "Unit History"}
          </h1>
          <p className="text-manuscript-primary/70">
            Explore the origins, evolution, and interesting facts about {unitName || "this unit"}.
          </p>
        </header>

        <main className="space-y-6">
          {historyText ? (
            <Card className="bg-manuscript-background/90 border border-manuscript-primary/10">
              <CardContent className="prose prose-manuscript p-6">
                <h2 className="text-2xl font-cinzel text-manuscript-primary mb-4">Historical Context</h2>
                <p className="text-manuscript-primary/90">{historyText}</p>
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-manuscript-background/90 border border-manuscript-primary/10">
              <CardContent className="p-6">
                <p className="text-manuscript-primary/90">
                  No specific historical context found for {unitName}.
                </p>
              </CardContent>
            </Card>
          )}

          {funFacts.length > 0 && (
            <Card className="bg-manuscript-background/90 border border-manuscript-primary/10">
              <CardContent className="p-6">
                <h2 className="text-2xl font-cinzel text-manuscript-primary mb-4">Fun Facts</h2>
                <ul className="list-disc list-inside text-manuscript-primary/90">
                  {funFacts.map((fact, index) => (
                    <li key={index}>{fact}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {!historyText && funFacts.length === 0 && (
            <Card className="bg-manuscript-background/90 border border-manuscript-primary/10">
              <CardContent className="p-6">
                <p className="text-manuscript-primary/90">
                  We're still researching the history and fun facts for {unitName}.
                  Please check back later!
                </p>
              </CardContent>
            </Card>
          )}

          <div className="my-16 py-8">
            <AdUnit 
              adSlot="3043090605"
              className="min-h-[250px] w-full bg-gray-100/50 rounded-lg"
              desktopOnly
            />
          </div>
        </main>
      </div>
    </div>
  );
}
